import React, { Dispatch, SetStateAction, useState } from 'react';
import { MenuItemVariantDto } from '../../../../../generated-api';
import { Dialog } from '@ordr-as/umami-react/dialog';
import { TextInput } from '@ordr-as/umami-react/form';
import { formatWithCurrency } from '../../../../utils/format';
import { useLocation } from '../../../../context/LocationContext';
import { useIntl } from 'react-intl';

interface Props {
  item: MenuItemVariantDto;
  customPrice: number | undefined;
  setCustomPrice: Dispatch<SetStateAction<number | undefined>>;
  setOpenCustomPriceDialog: Dispatch<SetStateAction<boolean>>;
  isOpen: boolean;
  addItemToCart: () => void;
}

const CustomPriceDialog = (props: Props) => {
  const { isOpen, item, customPrice, setCustomPrice, setOpenCustomPriceDialog, addItemToCart } = props;

  const { location } = useLocation();
  const intl = useIntl();
  const [inputIsValid, setInputIsValid] = useState(true);

  if (!isOpen) {
    return null;
  }

  const handleOnClose = () => {
    setCustomPrice(undefined);
    setOpenCustomPriceDialog(false);
  };

  const handleTextInputChange = (value: string) => {
    const parsedValue = parseFloat(value);
    const isNotvalid = isNaN(parsedValue) || parsedValue < 1;

    setInputIsValid(!isNotvalid);

    if (isNotvalid || parsedValue === item.price) {
      setCustomPrice(undefined);
      return;
    }

    setCustomPrice(parsedValue);
  };

  const handleAddItemToCart = () => {
    addItemToCart();
    handleOnClose();
  };

  const defaultItemPrice = item.price === 0 ? null : item.price;

  const titleLabel = intl.formatMessage({ id: 'items.customPrice.dialog.title' }, { name: item.name });
  const actionLabel = customPrice
    ? intl.formatMessage({ id: 'items.customPrice.dialog.actionLabel.customPrice' }) +
      ` (${formatWithCurrency(customPrice, location.currency)})`
    : intl.formatMessage({ id: 'items.customPrice.dialog.actionLabel.standardPrice' }) +
      ` (${formatWithCurrency(item.price, location.currency)})`;

  return (
    <Dialog
      aria-label="Custom price dialog"
      alignTop
      title={titleLabel}
      onClose={handleOnClose}
      actionLabel={actionLabel}
      onAction={handleAddItemToCart}
      isDisabledAction={!inputIsValid}
    >
      <TextInput
        fullWidth
        required
        autoFocus={defaultItemPrice === null}
        label={intl.formatMessage({ id: 'generic.price' })}
        defaultValue={defaultItemPrice?.toString()}
        validationState={inputIsValid ? undefined : 'invalid'}
        onChange={handleTextInputChange}
      />
    </Dialog>
  );
};

export default React.memo(CustomPriceDialog);
